/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useState } from 'react';

import Button from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import ChevronDownIcon from '@atlaskit/icon/core/migration/chevron-down';
import CustomizeIcon from '@atlaskit/icon/core/migration/customize--preferences';
import { fg } from '@atlaskit/platform-feature-flags';
import Popup, { type PopupProps } from '@atlaskit/popup';
import { Box } from '@atlaskit/primitives/compiled';

import type {
	ColorAndComponents,
	ColorMode,
	Iconography,
	SectionIds,
	Typography,
	VisualRefreshMode,
} from '../types';

import AppearanceControlsForm from './form';

const styles = cssMap({
	formContainer: {
		maxWidth: '410px',
	},
});

export type AppearanceControlsButtonProps = {
	colorAndComponents?: ColorAndComponents;
	colorMode?: ColorMode;
	iconography?: Iconography;
	onColorAndComponentsChange?(colorAndComponents: ColorAndComponents): void;
	onColorModeChange?(colorMode: ColorMode): void;
	onIconographyChange?(typography: Iconography): void;
	onTypographyChange?(typography: Typography): void;
	popupPlacement?: PopupProps['placement'];
	onVisualRefreshModeChange?(visualRefreshMode: VisualRefreshMode): void;
	typography?: Typography;
	visualRefreshMode?: VisualRefreshMode;
	testId?: string;
	hiddenSections?: SectionIds;
};

/**
 * __Appearance controls button__
 *
 * The button that triggers the appearance controls to appear.
 */
const AppearanceControlsButton = ({
	colorAndComponents,
	colorMode,
	iconography,
	onColorAndComponentsChange,
	onColorModeChange,
	onIconographyChange,
	onTypographyChange,
	onVisualRefreshModeChange,
	popupPlacement,
	typography,
	visualRefreshMode,
	testId,
	hiddenSections,
}: AppearanceControlsButtonProps) => {
	const [isOpen, setIsOpen] = useState(false);

	const handleClick = useCallback(() => {
		setIsOpen((current) => !current);
	}, []);

	let indicatorText = '';
	if (visualRefreshMode === 'refreshed') {
		indicatorText = ' (Refreshed)';
	} else if (visualRefreshMode === 'custom') {
		indicatorText = ' (Custom)';
	}

	return (
		<Popup
			isOpen={isOpen}
			onClose={() => setIsOpen(false)}
			placement={popupPlacement}
			testId={testId}
			shouldFlip={false}
			content={() => (
				<Box xcss={styles.formContainer}>
					<AppearanceControlsForm
						colorAndComponents={colorAndComponents}
						colorMode={colorMode}
						iconography={iconography}
						onColorAndComponentsChange={onColorAndComponentsChange}
						onColorModeChange={onColorModeChange}
						onIconographyChange={onIconographyChange}
						onTypographyChange={onTypographyChange}
						onVisualRefreshModeChange={onVisualRefreshModeChange}
						typography={typography}
						visualRefreshMode={visualRefreshMode}
						testId={testId ? `${testId}--form` : undefined}
						hiddenSections={hiddenSections}
					/>
				</Box>
			)}
			trigger={(triggerProps) => (
				<Button
					{...triggerProps}
					iconBefore={CustomizeIcon}
					iconAfter={(iconProps) => <ChevronDownIcon {...iconProps} size="small" />}
					appearance="subtle"
					isSelected={isOpen}
					onClick={handleClick}
					testId={testId ? `${testId}--button` : undefined}
				>
					Styles{indicatorText}
				</Button>
			)}
			shouldRenderToParent={fg('should-render-to-parent-should-be-true-design-syst')}
		/>
	);
};

export default AppearanceControlsButton;
